import React, { useEffect, useState } from 'react';

import { Button, useDeviceType } from '@braskai/ui-kit';

import { Box, Stack, Typography } from '@mui/material';

import { BookADemoBanner } from '@/features/bookADemoBanner';
import { consumeLandingProgressFloor } from '@/features/landingFlow/model/landingFlowStorage';
import { useTaskExecutionPercent } from '@/features/project/projectState';

import { ProjectTaskType } from '@/entities/project';
import { useCurrentUser } from '@/entities/user/currentUser';

import FullTutorialPreviewImg from '@/shared/assets/images/full-tutorial-preview.png';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { Project } from '@/shared/types/Project';
import { NativeVideoPlayer } from '@/shared/ui/NativeVideoPlayer';

import { useEmailConsent } from './hooks/useEmailConsent';

type TrialProjectProcessingOverrideProps = {
  overridePercent: number;
};

type TrialProjectProcessingProjectProps = {
  isFinalStatus?: boolean;
  status: Project['status'];
  statusStartDate: Project['status_date'];
  taskType: Exclude<ProjectTaskType, 'voiceUpdate'>;
  videoDuration: Project['duration'];
};

type TrialProjectProcessingProps = TrialProjectProcessingOverrideProps | TrialProjectProcessingProjectProps;

const HEADER_OFFSET = '73px';

const TrialProjectProcessingView: React.FC<{ headerOffset?: boolean; percent: number }> = ({
  headerOffset,
  percent,
}) => {
  const [isPlayed, setIsPlayed] = useState(false);
  const { data: user } = useCurrentUser();
  const { giveEmailConsent, isLoading } = useEmailConsent();
  const { isMobile } = useDeviceType();

  useEffect(() => {
    MixpanelEvents.trialProcessingScreenView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGiveEmailConsent = async () => {
    await giveEmailConsent();
  };

  const handlePlay = () => {
    if (!isPlayed) {
      setIsPlayed(true);
    }
  };

  const poster = FullTutorialPreviewImg;
  const videoOptions = {
    autoPlay: true,
    controls: true,
    loop: true,
  };

  const hasEmailConsent = user?.email_consent === true;

  const processingSection = (
    <Stack alignItems="center" direction="column" marginTop={isMobile ? 40 : 60} paddingX={isMobile ? 16 : 0}>
      <Stack alignItems="center" direction="row" spacing={4}>
        <Typography variant={isMobile ? 'title-4-md' : 'title-3-md'}>Generating video</Typography>
        <Typography color="primary.500" variant={isMobile ? 'title-4-md' : 'title-3-md'}>
          {percent}%
        </Typography>
      </Stack>

      {hasEmailConsent && (
        <Box marginTop={6} maxWidth={isMobile ? 265 : '100%'} textAlign={isMobile ? 'center' : 'left'}>
          <Typography color="grey.500" textAlign={isMobile ? 'center' : 'left'} variant="body-2-md">
            📥 We'll email you when it's ready, feel free to come back later!
          </Typography>
        </Box>
      )}

      {!hasEmailConsent && (
        <Stack alignItems="center" direction="column" marginTop={16} spacing={16}>
          <Stack alignItems="center" direction="column" spacing={6}>
            <Typography color="grey.900" variant="title-4-md">
              ✉️ No wait, we'll email you when it's ready.
            </Typography>
            <Typography color="grey.500" textAlign={isMobile ? 'center' : 'left'} variant="body-2-md">
              Only useful info like video notifications special offers and product updates
            </Typography>
          </Stack>

          <Button disabled={isLoading} isLoading={isLoading} variant="primary" onClick={handleGiveEmailConsent}>
            Notify me by email
          </Button>
        </Stack>
      )}
    </Stack>
  );

  const videoSection = (
    <Stack alignItems="center" direction="column" marginTop={isMobile ? 16 : 40} paddingX={isMobile ? 16 : 0}>
      <NativeVideoPlayer
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 16,
          maxHeight: 320,
          maxWidth: 580,
          objectFit: 'cover',
        }}
        poster={poster}
        src="https://static.rask.ai/raskapp/how_to_get_started_tutorial.mp4"
        onTimeUpdate={handlePlay}
        {...videoOptions}
      />
    </Stack>
  );

  return (
    <Box paddingTop={headerOffset ? HEADER_OFFSET : 0}>
      <Box marginTop={10} paddingX={isMobile ? 16 : 10} width="100%">
        <BookADemoBanner />
      </Box>
      {isMobile ? videoSection : processingSection}
      {isMobile ? processingSection : videoSection}
    </Box>
  );
};

const TrialProjectProcessingWithProject: React.FC<TrialProjectProcessingProjectProps> = ({
  isFinalStatus,
  status,
  statusStartDate,
  taskType,
  videoDuration,
}) => {
  const [progressFloor] = useState(() => consumeLandingProgressFloor());
  const progress = useTaskExecutionPercent({
    isFinalStatus,
    status,
    statusStartDate,
    taskType,
    videoDuration,
  });

  const percent = Math.max(progressFloor, progress.percent);

  return <TrialProjectProcessingView percent={percent} />;
};

export const TrialProjectProcessing: React.FC<TrialProjectProcessingProps> = (props) => {
  if ('overridePercent' in props) {
    const { overridePercent } = props;

    return <TrialProjectProcessingView percent={overridePercent} headerOffset />;
  }

  return <TrialProjectProcessingWithProject {...props} />;
};
